import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DeclareEmergencyBypassDto,
  EmergencyBypassDetail,
  RevokeEmergencyBypassDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';
import { filesKeys } from './files-api';
import { deadlinesKeys } from './deadlines-keys';

/** s. 144(9) declarations — every mutation returns the file's full list. */
export async function listEmergencyBypasses(fileId: string): Promise<EmergencyBypassDetail[]> {
  const { data } = await api.get<EmergencyBypassDetail[]>(`/files/${fileId}/emergency-bypass`);
  return data;
}

export const emergencyBypassKeys = {
  byFile: (fileId: string) => ['emergency-bypass', fileId] as const,
};

export function useEmergencyBypasses(fileId: string) {
  return useQuery({
    queryKey: emergencyBypassKeys.byFile(fileId),
    queryFn: () => listEmergencyBypasses(fileId),
    enabled: !!fileId,
  });
}

/**
 * Declaring or withdrawing changes the file's own emergency flag and status, and
 * starts or stops a statutory clock — so the file and the deadlines both have to
 * be re-fetched, not just this list.
 */
function useEmergencyMutation<TVars>(
  fileId: string,
  fn: (vars: TVars) => Promise<EmergencyBypassDetail[]>,
  successKey: string,
  // Follows the action, not the module: telling an officer their declaration
  // "could not be saved" when a withdrawal failed describes the wrong thing.
  errorKey: string,
) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: (rows) => {
      qc.setQueryData(emergencyBypassKeys.byFile(fileId), rows);
      qc.invalidateQueries({ queryKey: filesKeys.detail(fileId) });
      qc.invalidateQueries({ queryKey: deadlinesKeys.all });
      toast.success(successKey);
    },
    onError: (err) => toast.error(err, errorKey),
  });
}

export function useDeclareEmergencyBypass(fileId: string) {
  return useEmergencyMutation(
    fileId,
    async (dto: DeclareEmergencyBypassDto) => {
      const { data } = await api.post<EmergencyBypassDetail[]>(
        `/files/${fileId}/emergency-bypass`,
        dto,
      );
      return data;
    },
    'gov.emergency.savedToast',
    'gov.emergency.saveError',
  );
}

export function useRevokeEmergencyBypass(fileId: string) {
  return useEmergencyMutation(
    fileId,
    async ({ id, dto }: { id: string; dto: RevokeEmergencyBypassDto }) => {
      const { data } = await api.post<EmergencyBypassDetail[]>(
        `/files/${fileId}/emergency-bypass/${id}/revoke`,
        dto,
      );
      return data;
    },
    'gov.emergency.revokedToast',
    'gov.emergency.revokeError',
  );
}
